import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Form/form";
import Input from "@/components/Input/Input";
import { InputPostalCode } from "@/components/Input/PostalCode/InputPostalCode";
import { VStack } from "@/components/Layout";
import SelectCountry from "@/components/Select/SelectCountry";
import SelectStateProvince from "@/components/Select/SelectStateProvince";
import type { SupportedCountry } from "@/constants/country";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { AddressFormFieldProps, AddressFormValues } from "../-types";
import { useAddressFormContext } from "../useAddressFormContext";

export function JapaneseAddressForm({
  isCountryRequired,
  isStateProvinceRequired,
  isCityRequired,
  isStreetAddressRequired,
  isPostalCodeRequired,
}: AddressFormFieldProps) {
  const { values, errors, touched, onChange, onBlur, validate } =
    useAddressFormContext();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.addressForm",
  });

  const handlePostalCodeChange = (value: string) => {
    onChange({ postalCode: value });
  };

  const handleStateProvinceChange = (value: string) => {
    onChange({ stateProvinceCode: value });
    // provinceCode では onChange と同時に onBlur が発生し、古いステートをもとに validation が実行されてしまうため、
    // 明示的にここで最新の address をもとに validation を実行する
    validate({ ...values, stateProvinceCode: value });
  };

  const handleChange =
    <K extends keyof AddressFormValues>(name: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ [name]: e.target.value });
    };

  const handleBlur =
    <K extends keyof AddressFormValues>(name: K) =>
    () => {
      onBlur(name);
    };

  return (
    <VStack className="gap-4">
      <FormField
        name="postalCode"
        error={errors.postalCode}
        touched={touched.postalCode}
        required={isPostalCodeRequired}
      >
        <FormItem>
          <FormLabel>{t("postalCode")}</FormLabel>
          <FormControl>
            <InputPostalCode
              countryCode={values.countryCode}
              value={values.postalCode ?? ""}
              onValueChange={handlePostalCodeChange}
              onBlur={handleBlur("postalCode")}
              error={!!errors.postalCode && touched.postalCode}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        name="stateProvinceCode"
        error={errors.stateProvinceCode}
        touched={touched.stateProvinceCode}
        required={isStateProvinceRequired}
      >
        <FormItem>
          <FormLabel>{t("stateProvince")}</FormLabel>
          <FormControl>
            <SelectStateProvince
              country={values.countryCode}
              value={values.stateProvinceCode}
              onValueChange={handleStateProvinceChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        name="city"
        error={errors.city}
        touched={touched.city}
        required={isCityRequired}
      >
        <FormItem>
          <FormLabel>{t("city")}</FormLabel>
          <FormControl>
            <Input
              value={values.city}
              onChange={handleChange("city")}
              onBlur={handleBlur("city")}
              placeholder={t("cityPlaceholder")}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        name="streetAddress"
        error={errors.streetAddress}
        touched={touched.streetAddress}
        required={isStreetAddressRequired}
      >
        <FormItem>
          <FormLabel>{t("streetAddressInput")}</FormLabel>
          <FormControl>
            <Input
              value={values.streetAddress}
              onChange={handleChange("streetAddress")}
              onBlur={handleBlur("streetAddress")}
              placeholder={t("streetAddressInputPlaceholder")}
              width=""
              className="max-w-[400px]"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        name="countryCode"
        error={errors.countryCode}
        touched={touched.countryCode}
        required={isCountryRequired}
      >
        <FormItem>
          <FormLabel>{t("country")}</FormLabel>
          <FormControl>
            <SelectCountry
              value={values.countryCode}
              onChange={(value?: keyof typeof SupportedCountry) => {
                onChange({
                  countryCode: value,
                  postalCode: "",
                  stateProvinceCode: "",
                  city: "",
                  streetAddress: "",
                  addressLine2: "",
                });
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </VStack>
  );
}
